/**
 * Темы во вкладке слоями (#2072, спека docs/plans/2026-10-11-voice-report-layers.md
 * §2.1, §2.6) — общее у «Продаж» и «Сайта»: один ответ аналитики тем на вкладку
 * (сводка и раздел читают его, а не свои окна) и решение, есть ли раздел «Темы»
 * и карточка темы.
 *
 * Пустой набор решает список набора — тот же, что у кнопки «Задать темы» в
 * шапке, и не зависящий от периода: раздел не мигает при смене фильтра, а
 * кнопка и раздел не расходятся, пока аналитика перечитывается. Пока список
 * не прочитан, раздела нет; список не прочитался — решают строки аналитики.
 * Демо-набора (#2008) в списке нет — при показанном демо решает ответ
 * аналитики (`themeSectionSetEmpty`), а кнопка остаётся «Задать темы»: свой
 * набор у клиента пуст.
 *
 * Карточке нужен ещё разговор с темой в окне и, на «Сайте», срез «вопросы без
 * ответа», которого сервер не даёт читателю без права на текст разговоров.
 */
import type { VoiceThemeCuts, VoiceThemes } from '@aica/api-types/schemas';
import { useStreamDemoFilter } from '../stream-filter/useStreamDemoFilter';
import { useVoiceThemes, useVoiceThemesAvailable } from './themes/useVoiceThemes';
import { useVoiceThemeAnalytics } from './useVoiceOfCustomer';
import type { VoiceSearch } from './voice-search';
import {
  summaryCardState,
  type SummaryCardState,
  type SummaryLeadResult,
} from './voice-summary-view-model';
import {
  themeSectionSetEmpty,
  themeSetEmpty,
  themesHaveUnanswered,
  themesState,
  themesWithoutCalls,
} from './voice-themes-view-model';

export function useVoiceThemesLayer(
  search: VoiceSearch,
  onSearchChange: (patch: Partial<VoiceSearch>) => void,
  /** The tab's cut; none — the rows only (the theme picker on the other tabs). */
  cut: VoiceThemeCuts | undefined,
  lead: (themes: VoiceThemes) => SummaryLeadResult,
  { keepPrevious = false }: { keepPrevious?: boolean } = {}
) {
  const available = useVoiceThemesAvailable();
  const query = useVoiceThemeAnalytics(search, onSearchChange, cut, available, {
    keepPrevious,
  });
  const list = useVoiceThemes({ enabled: available });
  const { includeDemo } = useStreamDemoFilter(search, 'voice-section');

  // A failed refetch is not drawn as current (`VoiceThemesBlock` does the same).
  const state = query.data && !query.isError ? themesState(query.data) : null;
  const ready = state?.kind === 'ready' ? state.themes : undefined;
  const setEmpty = themeSectionSetEmpty({
    ready,
    demoShown: includeDemo === 'yes',
    list: list.data ? list.data.themes : list.isError ? 'failed' : undefined,
  });

  const shown = available && state?.kind !== 'closed' && setEmpty === false;
  const cardShown =
    shown &&
    !(
      ready &&
      (themesWithoutCalls(ready) || (cut === 'questions' && !themesHaveUnanswered(ready)))
    );
  const card: SummaryCardState = summaryCardState(
    {
      data: state ?? undefined,
      isPending: query.isPending,
      isError: query.isError,
      isPlaceholderData: query.isPlaceholderData,
      answer: query.data,
    },
    (current) => (current.kind === 'ready' ? lead(current.themes) : undefined)
  );
  // The block in the tab's export (#2009) while the tab shows it — not under a
  // theme filter: the filter does not narrow it, and a file filed under the
  // theme would pass the whole set off as the theme's conversations. Shown, it
  // holds the menu like every block: failed — «did not load», on its way — wait.
  const exportShown = shown && search.voiceTheme === undefined;
  const exported = {
    failed: exportShown && (query.isError || state?.kind === 'failed'),
    /** Nothing to wait for: not exported, or its answer came. */
    settled: !exportShown || ready !== undefined,
    /** The rows to export; an empty set draws no block and exports none. */
    themes: exportShown && ready && !themeSetEmpty(ready) ? ready : undefined,
  };
  /** The set's rows of the current answer — not a failed refetch's (`ready`). */
  return { available, query, list, ready, shown, cardShown, card, exported };
}

export type VoiceThemesLayer = ReturnType<typeof useVoiceThemesLayer>;
